import React, { useState, useEffect, useRef, useCallback } from 'react';
import Masonry from 'react-masonry-css';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Container,
  Grid,
  Paper,
  Button,
  Chip,
  Avatar,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Skeleton,
  Fab,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  AutoFixHigh as RewriteIcon,
  Close as CloseIcon,
  FavoriteBorderOutlined,
  ChatBubbleOutline,
  Share,
  ThumbUp,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  ContentCopy as CopyIcon,
  Sort as SortIcon,
  Article as ArticleIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  ViewCarousel as CarouselIcon,
  Description as DocumentIcon,
  OpenInNew as OpenInNewIcon,
  Send as SendIcon,
  Comment as CommentIcon
} from '@mui/icons-material';
import axios from 'axios';
import { alpha } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import AnimatedLogo from '../components/common/AnimatedLogo';
import PostCard from '../components/PostCard';
import FeatureLockOverlay from '../components/FeatureLockOverlay';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { Helmet } from 'react-helmet-async';

// Import the api utility
import api from '../utils/api';
import LoadingScreen from '../components/common/LoadingScreen'; // Added import

// Import SVG as ReactComponent
import { ReactComponent as PostReactions } from './post-reactions.svg';

// Import masonry styles
import './MasonryStyles.css';

// Set pdf.js worker source
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Add categories array after the imports
const categories = [
  { id: 'all', label: 'All Categories' },
  { id: 'agency-owners', label: 'Agency Owners' },
  { id: 'business-finance', label: 'Business Finance' },
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'consumer-insights', label: 'Consumer Insights' },
  { id: 'content-marketing', label: 'Content Marketing' },
  { id: 'copywriting', label: 'Copywriting' },
  { id: 'crypto-web3', label: 'Crypto/Web3' },
  { id: 'customer-success', label: 'Customer Success' },
  { id: 'data-analytics', label: 'Data Analytics' },
  { id: 'design', label: 'Design' },
  { id: 'digital-marketing', label: 'Digital Marketing' },
  { id: 'ecommerce-dtc', label: 'E-commerce & DTC' },
  { id: 'email-marketing', label: 'Email Marketing' },
  { id: 'gaming', label: 'Gaming' },
  { id: 'human-resources', label: 'Human Resource' },
  { id: 'i-gaming', label: 'I-Gaming' },
  { id: 'lead-generation', label: 'Lead Gen' },
  { id: 'leadership', label: 'Leadership' },
  { id: 'mental-health', label: 'Mental Health' },
  { id: 'mobile-development', label: 'Mobile Development' },
  { id: 'paid-ads', label: 'Paid Ads' },
  { id: 'personal-branding', label: 'Personal Branding' },
  { id: 'personal-finance-investing', label: 'Personal Finance & Investing' },
  { id: 'productivity', label: 'Productivity' },
  { id: 'real-estate', label: 'Real Estate' },
  { id: 'saas-founders', label: 'SaaS Founders' },
  { id: 'sales', label: 'Sales' },
  { id: 'seo', label: 'SEO' },
  { id: 'sporttech', label: 'SportTech' },
  { id: 'startups-entrepreneurship', label: 'Startups & Entrepreneurship' },
  { id: 'story-telling', label: 'Story Telling' },
  { id: 'tech-industry', label: 'Tech Industry' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'venture-capital', label: 'Venture Capital' },
  { id: 'wellness', label: 'Wellness' }
];

// Add the comment prompts after categories array
const commentPrompts = {
  "Simple Tone": "The above is a LinkedIn post, respond to the post professionally.",
  "New Insight": "The above is a LinkedIn post, and I would like to comment on it to engage with the author. The author is a senior executive at a large enterprise. I have never met and spoken with the author before, so our engagement must be professional and casual. Please don't be rude.\n\nThe objective is to share new insight so I can be seen as authoritative in the topic discussed in the post. The suggested comment will include brand-new insights in response to the post above. It should also include one example used in the industry. Use the first-person point of view in the comment. Do not paraphrase. Use new words, phrases, ideas, and insights with a new angle.\n\nBe concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited. You can use hyphens but make sure not to use en dashes, and em dashes. Keep the output under 20 words. Please don't use emojis or hashtags. Avoid any imbalanced quotation in the comment.",
  "Sell": "The above is a LinkedIn post, and I would like to comment on it to engage with the author. The author is a senior executive at a large enterprise. I have messaged and emailed the author, so the engagement can be reasonably casual but must remain professional.\n\nThe objective is to share my expertise so they know I can deliver results that meet their expectations.\n\nThe suggested comment will include an example used in the industry as discussed in the post. Please also provide social proof by casually mentioning that I have repeatedly solved this for our clients. Finish the comment by saying I will send them a DM about a case study they may find helpful. Use the first-person point of view in the comment.\n\nBe concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nDo not use hyphens en-dash, and em-dash. Please keep the comment under 75 words, and don't use emojis or hashtags. Avoid any imbalance quotations in the comment.",
  "Industry Leader": "The above is a LinkedIn post, and I would like to comment on it to engage with the author and position myself as an expert for the topic discussed. The author is a senior executive at a large enterprise.\n\nThe objective is to share my business acumen, technical know-how and expertise. The suggested comment will mention a book or research I have authored on the topic discussed in the post. It should also include an example of its application in the industry. Use the first-person point of view in the comment.\n\nAsk for their permission for me to send them a DM, if they would be interested in a free copy as a gift.\n\nBe concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nPlease keep the output under 45 words, and don't use emojis or hashtags. Avoid any imbalance quotation in the generated comment.",
  "Lighthearted": "The above is a LinkedIn post, and I would like to comment on it to build rapport with the author.\n\nBe a cheerful and light-hearted LinkedIn user. Reply to this LinkedIn post with a comment that contains a touch of humor or amusement while still being respectful and relevant. Go beyond jokes involving coffee and try a variety of broader topics. I want the joke to apply to the context of the content above.\n\nPlease keep the comment under 75 words, and include appropriate emojis or hashtags. Avoid any imbalance quotations in the generated comment.",
  "Reply Comment": "The above is a comment replied to on a LinkedIn post, and I would like to respond to it and engage with the commenter. The author is a senior executive at a large enterprise.\n\nThe objective is to start a conversation where we can continue by moving it to a DM.\n\nHere is how the comment should be structured:\n\nStart by thanking the person for sharing their thoughts.\n\nDo not paraphrase, but use new words, phrases, ideas, and insights from a new angle to add one insight in a single sentence. Subsequently, I would like to ask them to elaborate on their suggestions so I can understand their perspective. Use the first-person point of view in the comment.\n\nMake sure to avoid repeating what has already been said in the LinkedIn post. You can use hyphens but make sure not to use en dashes, and em dashes. Be concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nDo not include hashtags or emojis. Avoid any imbalance quotations in the generated comment. Please keep it to less than 50 words.",
  "Repost": "The above is a LinkedIn post, and I would like to build rapport with the author by resharing their content because I found the information to be valuable and inspiring.\n\nStart the message by giving credit to the author. Highlight one of the most crucial ideas suggested in the original content. Highlight why we can trust the author by quoting their background. Explain why I found it inspiring by quoting their insights and background information. Use the first-person point of view in the comment.\n\nI will share the post with my targeted audience, which is senior executives at large enterprises. Remember, this is a professional post, so refrain from using emojis to maintain a serious tone. You can use hyphens but make sure not to use en dashes, and em dashes. Be concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nThe output is around 150 to 200 words long. No emojis but we can include appropriate hashtags relevant to the content to increase content discovery by the LinkedIn algorithm. Avoid any imbalanced double quotation in the generated comment.",
  "Answer a Question": "The above is a LinkedIn post, and I would like to build rapport and start a conversation with the author by answering the question in their post.\n\nStart by identifying the question left within the post. Once you identify the question, craft me a comment to answer the question so I can join the conversation. Elaborate the answer with some of my personal experiences to make it more relatable. Use the first-person point of view in the comment.\n\nDo not repeat the question you found but provide an answer for the question.\n\nDo not paraphrase but use new words, phrases, ideas, and insights with a new angle. Be concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nYou can use hyphens but make sure not to use en dashes, and em dashes. Keep the output in less than 45 words.\n\nNo emoji and no hashtags. Avoid any imbalance quotations in the generated comment.",
  "Ask a Question": "The above is a LinkedIn post, and I would like to build rapport and start a conversation with the author by asking a question that's relevant to their post.\n\nStart by understanding the context of the content and the author's author's background.\n\nOnce you have the full context, craft me a question as a comment to the author. Keep the question suggestions short and simple. Use the first-person point of view in the comment.\n\nBe concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nYou can use hyphens but make sure not to use en dashes, and em dashes. Keep the output in less than 45 words.\n\nNo emoji and no hashtags. Avoid any imbalance quotations in the generated comment.",
  "Support Author": "The above is a LinkedIn post, and I would like to build rapport with the author by supporting them on LinkedIn.\n\nWrite me a straightforward sentence describing I'm commenting for broader reach so people could see their post. Avoid any imbalance quotation in the generated comment.",
  "Share Resume": "The above is a LinkedIn post, and I would like to comment and position myself as an expert for the topic discussed so that I can get a job at their company. The author is a hiring manager for the company for which I want to work.\n\nThe objective is to share new insight so I can be seen as authoritative in the topic discussed in the post. The suggested comment will include brand-new insights in response to the post above. Ideally, it should also include one example used in the industry. Finally, let them know that I'm looking for a new opportunity and it is ok if I send them my resume by a DM. Use the first-person point of view in the comment.\n\nDo not paraphrase. Use new words, phrases, ideas, and insights with a new angle.\n\nBe concise, and go straight to the point. Do not beat around the bush. Do not overly agree or act excessively excited.\n\nPlease keep the output under 60 words, and don't use emojis or hashtags. You can use hyphens but make sure not to use en dashes, and em dashes. Avoid any imbalance quotation in the generated comment."
};

const PDFViewer = ({ url }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  console.log('PDFViewer rendering with URL:', url); // Debug log

  const onDocumentLoadSuccess = ({ numPages }) => {
    console.log('PDF loaded successfully with', numPages, 'pages');
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    setError('Failed to load PDF');
    setLoading(false);
  };

  if (!url) {
    return <Typography color="error">No PDF URL provided</Typography>;
  }

  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      bgcolor: '#f5f5f5',
      borderRadius: 1,
      p: 2
    }}>
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ mt: 1 }}>Loading PDF...</Typography>
        </Box>
      )}
      
      {error ? (
        <Typography color="error" sx={{ my: 2 }}>{error}</Typography>
      ) : (
        <>
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ mt: 1 }}>Loading PDF...</Typography>
              </Box>
            }
          >
            <Page 
              pageNumber={pageNumber} 
              renderTextLayer={false}
              renderAnnotationLayer={false}
              width={Math.min(window.innerWidth * 0.8, 800)}
            />
          </Document>
          
          {numPages > 0 && (
            <Box sx={{ 
              mt: 2, 
              display: 'flex', 
              alignItems: 'center',
              gap: 2
            }}>
              <Button
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
                variant="outlined"
                size="small"
              >
                Previous
              </Button>
              
              <Typography>
                Page {pageNumber} of {numPages}
              </Typography>
              
              <Button
                disabled={pageNumber >= numPages}
                onClick={() => setPageNumber(prev => Math.min(prev + 1, numPages))}
                variant="outlined"
                size="small"
              >
                Next
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

const ViralLinkedInPosts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [openPostDetails, setOpenPostDetails] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [rewrittenContent, setRewrittenContent] = useState('');
  const [rewriteTopic, setRewriteTopic] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [openRewritePanel, setOpenRewritePanel] = useState(false);
  const [sortOption, setSortOption] = useState('recent');
  const [expandedPosts, setExpandedPosts] = useState({});
  const [filters, setFilters] = useState({ format: 'all' });
  const { enqueueSnackbar } = useSnackbar();
  const [profileUrl, setProfileUrl] = useState('abdulsubhanf');
  const [fetching, setFetching] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentingPost, setCommentingPost] = useState(null);
  const [commentLoading, setCommentLoading] = useState(false);

  // New state for subscription check
  const [subscriptionStatusLoading, setSubscriptionStatusLoading] = useState(true);
  const [isFeatureAllowed, setIsFeatureAllowed] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState(null);

  // Plan IDs from environment variables (ensure these are set in your .env and exposed to React)
  const PRO_PLAN_ID = process.env.REACT_APP_PADDLE_PRO_MONTHLY;
  const PRO_YEARLY_ID = process.env.REACT_APP_PADDLE_PRO_YEARLY;
  const BUSINESS_PLAN_ID = process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY;
  const BUSINESS_YEARLY_ID = process.env.REACT_APP_PADDLE_BUSINESS_YEARLY;

  // Add new state variables for AI comment generation
  const [selectedCommentPrompt, setSelectedCommentPrompt] = useState("Simple Tone");
  const [generatingComment, setGeneratingComment] = useState(false);
  const [openPromptDropdown, setOpenPromptDropdown] = useState(false);

  useEffect(() => {
    const checkSubscription = async () => {
      setSubscriptionStatusLoading(true);
      try {
        const response = await api.get('/api/subscriptions/status'); 
        const subscription = response.data.subscription;
        
        if (subscription && subscription.status === 'active') {
          setCurrentPlanId(subscription.price_id);
          if (
            subscription.price_id === PRO_PLAN_ID || 
            subscription.price_id === PRO_YEARLY_ID || 
            subscription.price_id === BUSINESS_PLAN_ID || 
            subscription.price_id === BUSINESS_YEARLY_ID
          ) {
            setIsFeatureAllowed(true);
          } else {
            setIsFeatureAllowed(false);
          }
        } else {
          setIsFeatureAllowed(false); // No active subscription or not Pro/Business
        }
      } catch (error) {
        console.error("Error fetching subscription status:", error);
        setIsFeatureAllowed(false); // Default to not allowed on error
        // Optionally, set an error state here to show a specific message
      } finally {
        setSubscriptionStatusLoading(false);
      }
    };

    checkSubscription();
  }, [PRO_PLAN_ID, PRO_YEARLY_ID, BUSINESS_PLAN_ID, BUSINESS_YEARLY_ID]); // Re-run if plan IDs were to change (though unlikely for env vars)

  useEffect(() => {
    // This effect now waits for subscription check to complete AND feature to be allowed
    if (!subscriptionStatusLoading && isFeatureAllowed) {
      console.log("Initial fetchPosts trigger from mount effect.");
      fetchPosts(true); // Pass true for initial load
    }

    // Add scroll listener for "back to top" button
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [subscriptionStatusLoading, isFeatureAllowed]); // Add dependencies: only run fetchPosts when allowed

  const fetchPosts = async (isNewSearch = true) => {
    // Check feature allowance first
    if (!isFeatureAllowed) {
      console.log("Feature not allowed, skipping fetchPosts.");
      setLoading(false); // Ensure loading is false if feature not allowed
      return;
    }
    if (subscriptionStatusLoading) {
      console.log("Subscription status loading, skipping fetchPosts for now.");
      return; // Don't fetch if still checking subscription
    }

    console.log(`fetchPosts triggered. isNewSearch: ${isNewSearch}, page: ${page}, sortOption: ${sortOption}, selectedCategory: ${selectedCategory}, formatFilter: ${filters.format}`);
    setLoading(true); // Set loading true at the start
    setError(null); // Clear previous errors
    // Don't reset posts here, only on successful fetch or explicit clear
    // setFetching(true); // This state seems unused? Maybe remove later.

    try {
      const token = localStorage.getItem('token'); // Keep token for potential direct use or debugging
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      // Use the correct API base URL with fallback
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.instation.app';
      const backendUrl = window.location.origin.includes('localhost') 
        ? 'http://localhost:5000' 
        : apiUrl;
      
      console.log('Fetching LinkedIn posts from database');
      console.log('Selected category:', selectedCategory);
      
      // Format the category to match the database format
      const formattedCategory = selectedCategory === 'all' ? 'all' : 
        selectedCategory === 'startups-entrepreneurship' ? 'Startups & Entrepreneurship' :
        selectedCategory.split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      
      console.log('Formatted category:', formattedCategory);
      
      // Construct endpoint path relative to the base URL handled by the api utility
      const endpointPath = `/api/linkedin/influencer-posts${formattedCategory !== 'all' ? `?category=${encodeURIComponent(formattedCategory)}` : ''}`;
      
      console.log('Calling endpoint path:', endpointPath);
      
      // USE the api utility for the request
      const response = await api.get(endpointPath);
      
      console.log('Raw backend response:', JSON.stringify(response.data.posts, null, 2));
      
      if (!response.data) {
        throw new Error('Failed to fetch LinkedIn posts - Empty response');
      }
      
      // Extract posts array and counts from the response data
      let { posts: allPostsArray, totalCount, totalPosts } = response.data;
      
      if (!allPostsArray || allPostsArray.length === 0) {
        setError('No posts found in database.');
        return;
      }

      // Apply post type filter if selected
      if (filters.format && filters.format !== 'all') {
        allPostsArray = allPostsArray.filter(post => {
          const format = post.format || post.media_type;
          const hasDocument = post.document || post.carousel_document_link || 
                            (post.media && (post.media.type === 'Document' || post.media.type === 'document'));
          
          switch (filters.format) {
            case 'Image':
              return format === 'Image' || format === 'image' || 
                     (post.media && post.media.type === 'image' && !hasDocument);
            case 'Video':
              return format === 'Video' || format === 'video' || 
                     (post.media && post.media.type === 'video');
            case 'Document':
              return hasDocument;
            case 'Text':
              return (!format && !post.media) || format === 'Text' || 
                     (!post.media && !hasDocument);
            default:
              return true;
          }
        });
        
        // Update total count for filtered results
        totalCount = allPostsArray.length;
      }
      
      console.log(`Found ${totalCount} matching posts out of ${totalPosts} total posts in database`);
      
      // Apply sort before pagination
      const sortedPosts = sortPostsArray(allPostsArray, sortOption);
      
      // Apply pagination - 24 posts per page
      const postsPerPage = 24;
      const currentPageIndex = page - 1;
      const startIndex = currentPageIndex * postsPerPage;
      const endIndex = startIndex + postsPerPage;
      
      // Get posts for current page
      const paginatedPosts = sortedPosts.slice(startIndex, endIndex);
      
      // Calculate total pages based on totalCount from backend
      const calculatedTotalPages = Math.max(1, Math.ceil(totalCount / postsPerPage)); // Ensure totalPages >= 1
      console.log(`Calculated total pages: ${calculatedTotalPages} (${totalCount} matching posts / ${postsPerPage} per page)`);
      setTotalPages(calculatedTotalPages);
      
      // Ensure current page is within bounds
      if (page > calculatedTotalPages) {
        console.log(`Current page ${page} is out of bounds (${calculatedTotalPages} total pages). Resetting to page 1.`);
        // setLoading(false); // Set loading false before returning
        setPage(1); // This will trigger a re-fetch via useEffect
        return; // Exit early, the re-fetch will handle loading state
      }
      
      console.log(`Showing page ${page} (${startIndex + 1}-${Math.min(endIndex, totalCount)}) of ${calculatedTotalPages} pages`);
      console.log(`Paginated posts for this page: ${paginatedPosts.length}`);
      
      // Add debug logging for video detection
      const debugPost = (post) => {
        console.log('Post Debug:', {
          id: post.urn || post.id,
          media_type: post.media_type,
          media_url: post.media_url,
          format: post.format,
          mediaObject: post.media,
          raw_data: post.raw_data,
          media_details: {
            type: post.media?.type,
            url: post.media?.url,
            format: post.format,
            hasDocument: !!(post.media?.type === 'Document' || post.format === 'Document' || post.carousel_document_link)
          }
        });
      };

      // Create properly formatted posts for our UI
      const formattedPosts = paginatedPosts.map(post => {
        debugPost(post);  // Add debug logging
        
        // Try to get video data from raw_data first, then fall back to direct fields
        let mediaData = null;
        try {
          if (post.raw_data && typeof post.raw_data === 'string') {
            const rawData = JSON.parse(post.raw_data);
            mediaData = rawData.media;
          }
        } catch (e) {
          console.warn('Error parsing raw_data:', e);
        }

        // Get the best video URL and thumbnail
        const videoUrl = post.media_url || post.media?.url || mediaData?.url || '';
        const videoThumbnail = post.media_thumbnail || post.media?.thumbnail || mediaData?.thumbnail || post.thumbnail || '';

        // Determine if this is a video post
        const isVideoType = 
          (post.media_type === 'Video' || post.media_type === 'video') ||
          (post.media?.type === 'Video' || post.media?.type === 'video') ||
          (mediaData?.type === 'Video' || mediaData?.type === 'video');

        const hasVideoUrl = videoUrl && (
          videoUrl.includes('/playlist/vid/') ||
          videoUrl.includes('/vid/') ||
          videoUrl.includes('.mp4')
        );

        const isVideo = isVideoType || hasVideoUrl;

        // Determine if this is an image post
        const isImageType = 
          (post.media_type === 'Image' || post.media_type === 'image') ||
          (post.media?.type === 'Image' || post.media?.type === 'image') ||
          (mediaData?.type === 'Image' || mediaData?.type === 'image');

        const hasImageUrl = !isVideo && (post.media?.url || post.media_url || '').includes('/image/');
        const isImage = isImageType || hasImageUrl;

        // Create the media object
        const mediaObject = (() => {
          // Debug the media detection
          console.log('Media detection:', {
            isVideo,
            isImage,
            hasDocument: !!(post.media?.type === 'Document' || post.format === 'Document' || post.carousel_document_link),
            mediaType: post.media?.type,
            format: post.format,
            documentUrl: post.media?.url || post.carousel_document_link
          });

          if (isVideo) {
            return {
              type: 'video',
              url: videoUrl,
              thumbnail: videoThumbnail,
              items: []
            };
          }
          
          if (isImage) {
            return {
              type: 'image',
              url: post.media?.url || post.media_url || '',
              thumbnail: post.media?.thumbnail || post.media_thumbnail || post.thumbnail || '',
              items: []
            };
          }
          
          // Check for document first, before falling back to media_type
          if (post.media?.type === 'Document' || post.format === 'Document' || post.carousel_document_link) {
            console.log('Document detected:', {
              mediaType: post.media?.type,
              format: post.format,
              url: post.media?.url || post.carousel_document_link
            });
            
            return {
              type: 'Document',
              url: post.media?.url || post.carousel_document_link || '',
              thumbnail: post.media?.thumbnail || post.media_thumbnail || post.thumbnail || '',
              items: []
            };
          }
          
          if (post.media_type) {
            return {
              type: post.media_type,
              url: post.media_url || post.media?.url || post.carousel_document_link || '',
              thumbnail: post.media_thumbnail || post.media?.thumbnail || '',
              items: post.media?.items || []
            };
          }
          
          return null;
        })();

        console.log('Post media debug:', {
          id: post.id || post.urn,
          isVideo,
          isImage,
          mediaObject,
          originalMedia: post.media,
          carouselDocument: post.carousel_document_link
        });

        const formattedPost = {
          document: {
            id: post.urn || post.id || `post-${Math.random()}`,
            body: post.content || post.text || '', 
            likes_count: post.total_reactions || post.stats?.total_reactions || 0,
            comments_count: post.comments || post.stats?.comments || 0,
            platform_created_at: post.postedAt?.timestamp || 
                                (post.postedAt?.date ? new Date(post.postedAt.date).getTime() : null)
          },
          data: {
            author: {
              name: post.author_name || post.author?.name || (post.author?.first_name && post.author?.last_name ? `${post.author.first_name} ${post.author.last_name}`.trim() : 'LinkedIn User'),
              image_base64: '',
              occupation: post.author_headline || post.author?.headline || '',
              profilePicture: post.author_profile_picture || 
                             post.author?.profile_picture || 
                             post.author?.profilePicture || 
                             post.author?.image || 
                             post.author?.imageUrl || 
                             post.author?.pictureUrl || ''
            }
          },
          media: mediaObject,
          format: isVideo ? 'Video' : (isImage ? 'Image' : (post.carousel_document_link ? 'Document' : (post.media_type || post.format || 'Text'))),
          thumbnail: videoThumbnail || post.media_thumbnail || post.thumbnail || null,
          document_data: post.document,
          stats: post.stats,
          posted_at: post.postedAt,
          url: post.url,
          category: post.category,
          carousel_document_link: post.carousel_document_link
        };

        return formattedPost;
      });
      
      setPosts(formattedPosts);
      
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('No posts found. Please check again after some time');
      // Do NOT reset posts here, keep the old data or empty state
    } finally {
      console.log("fetchPosts finished, setting loading to false.");
      setLoading(false); // Ensure loading is set to false regardless of success/error/return path
      // setLoadingMore(false); // If this state is used, reset it too
      // setFetching(false); // If this state is used, reset it too
    }
  };

  useEffect(() => {
    // These effects also need to respect the subscription check
    if (!subscriptionStatusLoading && isFeatureAllowed) {
      // Avoid fetching if it's the initial mount (handled by the other effect) unless page/sort changed from default
      const isInitialMountDefaults = page === 1 && sortOption === 'recent';
      // TODO: This initial mount check might be overly complex or not quite right.
      // Consider if a simpler approach exists, e.g., using a ref to track initial load.
      // For now, let's assume any change triggers fetch if allowed.
      console.log(`fetchPosts trigger from page/sort effect. Page: ${page}, Sort: ${sortOption}`);
      fetchPosts(false); // Pass false for subsequent loads
    }
  }, [page, sortOption, subscriptionStatusLoading, isFeatureAllowed]); // Add dependencies

  // Add useEffect to refetch posts when category or format changes
  useEffect(() => {
    if (!subscriptionStatusLoading && isFeatureAllowed) {
      console.log(`fetchPosts trigger from category/format effect. Category: ${selectedCategory}, Format: ${filters.format}`);
      fetchPosts(true); // Treat category/format change as a new search
    }
  }, [selectedCategory, filters.format, subscriptionStatusLoading, isFeatureAllowed]); // Add dependencies

  const loadMorePosts = () => {
    // We don't need this for pagination, but keep a stub in case it's called elsewhere
    console.log('loadMorePosts called, but pagination is active. Use handlePageChange instead.');
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPosts();
  };

  const handlePostClick = (post) => {
    setSelectedPost(post);
    setRewrittenContent('');
    setRewriteTopic('');
    setOpenPostDetails(true);
    setOpenRewritePanel(false);
  };

  const handleCloseDetails = () => {
    setOpenPostDetails(false);
    setOpenRewritePanel(false);
  };

  const handleOpenRewritePanel = () => {
    setOpenRewritePanel(true);
    setOpenPostDetails(false);
  };

  const handleCloseRewritePanel = () => {
    setOpenRewritePanel(false);
  };

  const handleRewriteWithAI = async () => {
    if (!selectedPost) {
      enqueueSnackbar('No post selected', { variant: 'warning' });
      return;
    }
    
    setRewriteLoading(true);
    try {
      const postBody = selectedPost.document.body;
      
      // Prepare prompt for OpenAI with emphasis on preserving structure
      const topicText = rewriteTopic.trim() 
        ? `to make it about the topic: ${rewriteTopic}` 
        : `to improve its engagement while keeping the same topic`;

      const prompt = `Rewrite the following LinkedIn post ${topicText}.

IMPORTANT: Maintain the exact same structure, formatting, and style as the original post, including:
- Keep the same paragraph breaks
- Preserve any bullet points or lists in the same format
- Maintain the same number of paragraphs
- Keep similar sentence length and complexity
- Preserve any call-to-action elements
- Maintain the same tone (professional, casual, etc.)
- If the original post ends with a specific instruction or pointer, keep that pattern

Original post:
${postBody}

Your rewrite should feel like the original author wrote it${rewriteTopic.trim() ? ` but about ${rewriteTopic} instead` : ' with improved wording and engagement'}.`;
      
      // Call Azure OpenAI API
      const response = await fetch('https://eai.openai.azure.com/openai/deployments/test/chat/completions?api-version=2024-02-15-preview', {
        method: 'POST',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/135.0.0.0 Safari/537.36 Edg/135.0.0.0',
          'Accept': 'application/json',
          'Accept-Encoding': 'gzip, deflate, br, zstd',
          'api-key': process.env.REACT_APP_OPENAI_API_KEY,
          'content-type': 'application/json;charset=UTF-8',
          'origin': `chrome-extension://${process.env.REACT_APP_CHROME_EXTENSION_ID}`
        },
        body: JSON.stringify({
          "model": "gpt-4",
          "messages": [
            {
              "role": "user",
              "content": prompt
            }
          ],
          "temperature": 0.7,
          "max_tokens": 600
        })
      });
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Rewrite API Response:', data);
      
      // Extract the generated content
      const generatedText = data.choices[0].message.content;
      setRewrittenContent(generatedText);
      
      enqueueSnackbar('Post rewritten successfully!', { variant: 'success' });
    } catch (error) {
      console.error('Error rewriting post:', error);
      enqueueSnackbar('Error rewriting post. Please try again.', { variant: 'error' });
    } finally {
      setRewriteLoading(false);
    }
  };

  const handleCopyRewrite = () => {
    if (rewrittenContent) {
      navigator.clipboard.writeText(rewrittenContent);
      enqueueSnackbar('Copied to clipboard!', { variant: 'success' });
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num;
  };
  
  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Utility function to decode HTML entities in post content
  const decodeHtmlEntities = (text) => {
    if (!text) return '';
    
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    const result = textarea.value;
    textarea.remove();
    return result;
  };

  const toggleExpanded = (postId) => {
    setExpandedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const formatPostUrl = (post) => {
    // Check if the post already has a URL property
    if (post.url) return post.url;
    
    // Otherwise try to construct one from the URN/ID
    if (post.document && post.document.id) {
      const id = post.document.id;
      // If it's already in URN format with activity
      if (id.includes('urn:li:activity:')) {
        const activityId = id.replace('urn:li:activity:', '');
        return `https://www.linkedin.com/feed/update/urn:li:activity:${activityId}`;
      }
      // If it's just the activity ID
      if (/^\d+$/.test(id)) {
        return `https://www.linkedin.com/feed/update/urn:li:activity:${id}`;
      }
    }
    
    // Fallback - no URL available
    return null;
  };

  // Function to handle opening the comment dialog
  const handleOpenCommentDialog = (post) => {
    setCommentingPost(post);
    setCommentText('');
    setOpenCommentDialog(true);
  };

  // Function to handle closing the comment dialog
  const handleCloseCommentDialog = () => {
    setOpenCommentDialog(false);
    setCommentingPost(null);
    setCommentText('');
  };

  // Function to post a comment to LinkedIn
  const handlePostComment = async () => {
    if (!commentingPost || !commentText.trim()) {
      enqueueSnackbar('Please enter a comment', { variant: 'warning' });
      return;
    }

    // Check comment length against LinkedIn's maximum
    if (commentText.length > 700) {
      enqueueSnackbar('Comment is too long. LinkedIn allows maximum 700 characters', { variant: 'warning' });
      return;
    }

    setCommentLoading(true);
    try {
      // Extract the activity URN from the post
      const activityUrn = commentingPost.document.id;
      
      console.log('Posting comment to LinkedIn post:', {
        postId: activityUrn,
        commentLength: commentText.length
      });
      
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      // Use the correct API base URL with fallback
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.instation.app';
      const backendUrl = window.location.origin.includes('localhost') 
        ? 'http://localhost:5000' 
        : apiUrl;
      
      console.log('Using API URL:', `${backendUrl}/api/linkedin/post-comment`);
      
      // Make API call to LinkedIn to post the comment
      const response = await axios.post(
        `${backendUrl}/api/linkedin/post-comment`, 
        {
          activityUrn,
          comment: commentText
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        console.log('Comment posted successfully:', response.data);
        enqueueSnackbar('Comment posted successfully to LinkedIn!', { variant: 'success' });
        
        // Update local comment count
        if (commentingPost) {
          const updatedPosts = posts.map(post => {
            if (post.document.id === commentingPost.document.id) {
              return {
                ...post,
                document: {
                  ...post.document,
                  comments_count: (post.document.comments_count || 0) + 1
                }
              };
            }
            return post;
          });
          setPosts(updatedPosts);
          
          // If the post is selected and in detail view, update it too
          if (selectedPost && selectedPost.document.id === commentingPost.document.id) {
            setSelectedPost({
              ...selectedPost,
              document: {
                ...selectedPost.document,
                comments_count: (selectedPost.document.comments_count || 0) + 1
              }
            });
          }
        }
        
        handleCloseCommentDialog();
      } else {
        throw new Error(response.data.message || 'Failed to post comment');
      }
    } catch (error) {
      console.error('Error posting comment:', error);
      
      // Provide specific error messages based on error type
      if (error.response?.status === 401) {
        enqueueSnackbar('LinkedIn authentication failed. Please reconnect your LinkedIn account.', 
          { variant: 'error' });
      } else if (error.response?.status === 400) {
        enqueueSnackbar(error.response.data.message || 'Invalid comment or post data', 
          { variant: 'error' });
      } else if (error.response?.status === 429) {
        enqueueSnackbar('Too many requests to LinkedIn API. Please try again later.', 
          { variant: 'error' });
      } else if (error.response?.status === 404) {
        enqueueSnackbar('API endpoint not found. Please check server configuration.', 
          { variant: 'error' });
      } else {
        // Show full error details for debugging
        console.log('Full error details:', error.response?.data);
        enqueueSnackbar(
          error.response?.data?.message || error.message || 'Failed to post comment. Please try again.', 
          { variant: 'error' }
        );
      }
    } finally {
      setCommentLoading(false);
    }
  };

  // Add handleRemixClick function
  const handleRemixClick = (post) => {
    setSelectedPost(post);
    setRewrittenContent('');
    setRewriteTopic('');
    setOpenRewritePanel(true);
  };

  // Update the renderPostCard function
  const renderPostCard = (post, ref) => {
    return (
      <div ref={ref} style={{ marginBottom: '16px' }}>
        <PostCard
          post={post}
          onCommentClick={() => handleOpenCommentDialog(post)}
          onRemixClick={() => handleRemixClick(post)}
        />
      </div>
    );
  };

  const renderPostDetails = () => {
    if (!selectedPost) return null;
    
    const postData = selectedPost.document;
    const author = selectedPost.data?.author || { name: 'LinkedIn User' };
    const postUrl = formatPostUrl(selectedPost);
    
    // PDF detection
    const hasPDF = selectedPost.media?.type === 'Document' && 
                   selectedPost.media?.url?.includes('/document/');  // LinkedIn document URLs contain '/document/'

    console.log('PDF detection:', {  // Debug log
      mediaType: selectedPost.media?.type,
      url: selectedPost.media?.url,
      hasPDF
    });

    // Get thumbnail for the post
    const getThumbnailUrl = (post) => {
      // Log the media object for debugging in detail view
      console.log('Detail view - Media object:', JSON.stringify(post.media, null, 2));
      
      // Handle document thumbnail
      if (post.format === 'Document' || post.document_data) {
        return post.document_data?.thumbnail || post.thumbnail || post.media?.thumbnail || '';
      }
      
      // Handle image
      if (post.format === 'Image' && post.media) {
        return post.media.url || post.media.thumbnail || '';
      }
      
      // Handle video - just return the thumbnail, actual video handled separately
      if ((post.format === 'Video' || post.media?.type === 'video') && post.media) {
        return post.media.thumbnail || '';
      }
      
      // For carousel, show the first image
      if (post.format === 'Carousel' && post.media?.items && post.media.items.length > 0) {
        return post.media.items[0].url || post.media.items[0].thumbnail || '';
      }
      
      return ''; // No thumbnail
    };
    
    // Check if post has video
    const hasVideo = selectedPost.media && 
                   (selectedPost.format === 'Video' || 
                    selectedPost.media.type === 'video' || 
                    selectedPost.media_type === 'Video' ||
                    (selectedPost.media.url && (
                      selectedPost.media.url.includes('/vid/') || 
                      selectedPost.media.url.includes('/playlist/vid/') ||
                      selectedPost.media.url.includes('.mp4')
                    )));
    
    console.log(`Detail view - Post ${selectedPost.document?.id} - Format: ${selectedPost.format}, Media type: ${selectedPost.media?.type}, Has video: ${hasVideo}`);
    
    const thumbnailUrl = getThumbnailUrl(selectedPost);
    
    // Extract title - first line or first sentence of content
    const getPostTitle = (text) => {
      if (!text) return '';
      // Try to get first line
      const firstLine = text.split('\n')[0].trim();
      if (firstLine && firstLine.length > 5) return firstLine;
      
      // If first line is too short, try to get first sentence
      const firstSentence = text.split(/\.|\?|\!/)[0].trim();
      return firstSentence || '';
    };
    
    // Update the formatPostDate function to handle both timestamp and date string
    const formatPostDate = (timestamp) => {
      if (!timestamp) return '';
      
      let date;
      if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else {
        return '';
      }
      
      // Format like "Apr 14, 2025 at 10:03AM"
      const options = { month: 'short', day: 'numeric', year: 'numeric' };
      const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
      
      const dateStr = date.toLocaleDateString('en-US', options);
      const timeStr = date.toLocaleTimeString('en-US', timeOptions);
      
      return `${dateStr} at ${timeStr}`;
    };
    
    const postTitle = getPostTitle(postData.body);
    const postContent = postData.body?.replace(postTitle, '').trim();
    const postDate = formatPostDate(selectedPost.posted_at?.timestamp || selectedPost.posted_at?.date);
    
    return (
      <Dialog 
        open={openPostDetails} 
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            p: { xs: 1, sm: 1.5 },
            maxHeight: '90vh',
            height: isMobile ? '95vh' : 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          pb: 1,
          borderBottom: '1px solid #e0e0e0',
          px: { xs: 2, sm: 2.5 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar 
              src={author.profilePicture || (author.image_base64 ? `data:image/jpeg;base64,${author.image_base64}` : '')}
              alt={author.name}
              sx={{ width: 40, height: 40, mr: 1.5 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.2, fontSize: '1rem' }}>
                {author.name}
              </Typography>
              <Typography variant="caption" color="#666" sx={{ lineHeight: 1.3, display: 'block', mt: 0.3 }}>
                {postDate}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {(() => {
                // Determine content type icon
                if (selectedPost.media && selectedPost.media.type === 'document') {
                  console.log('Document type detected in detail view:', selectedPost.document?.id);
                  return (
                    <Tooltip title="Document">
                      <DocumentIcon fontSize="small" sx={{ color: '#0073b1', fontSize: '18px', stroke: '#ffffff', strokeWidth: 1 }} />
                    </Tooltip>
                  );
                } else if (hasVideo) {
                  return (
                    <Tooltip title="Video">
                      <VideoIcon fontSize="small" sx={{ color: '#0073b1', fontSize: '18px', stroke: '#ffffff', strokeWidth: 1 }} />
                    </Tooltip>
                  );
                } else if (selectedPost.format === 'Image' || 
                  (selectedPost.media && (selectedPost.media.type === 'image' || thumbnailUrl))) {
                  return (
                    <Tooltip title="Image">
                      <ImageIcon fontSize="small" sx={{ color: '#0073b1', fontSize: '18px', stroke: '#ffffff', strokeWidth: 1 }} />
                    </Tooltip>
                  );
                } else if (selectedPost.format === 'Document' || selectedPost.document_data) {
                  return (
                    <Tooltip title="Document">
                      <DocumentIcon fontSize="small" sx={{ color: '#0073b1', fontSize: '18px', stroke: '#ffffff', strokeWidth: 1 }} />
                    </Tooltip>
                  );
                } else if (selectedPost.format === 'Carousel' || 
                  (selectedPost.media && selectedPost.media.items && selectedPost.media.items.length > 0)) {
                  return (
                    <Tooltip title="Carousel">
                      <CarouselIcon fontSize="small" sx={{ color: '#0073b1', fontSize: '18px', stroke: '#ffffff', strokeWidth: 1 }} />
                    </Tooltip>
                  );
                } else {
                  return (
                    <Tooltip title="Text">
                      <ArticleIcon fontSize="small" sx={{ color: '#0073b1', fontSize: '18px', stroke: '#ffffff', strokeWidth: 1 }} />
                    </Tooltip>
                  );
                }
              })()}
          </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {postUrl && (
              <Tooltip title="Open in LinkedIn">
                <IconButton 
                  size="small"
                  onClick={() => window.open(postUrl, '_blank', 'noopener,noreferrer')}
                  sx={{ mr: 1 }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          <IconButton onClick={handleCloseDetails} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2, px: { xs: 2, sm: 2.5 } }}>
          {/* Post Title */}
          {postTitle && (
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                fontWeight: 500, 
                fontSize: '1.1rem',
                color: '#4e4e4e',
                fontStyle: 'italic'
              }}
            >
              {postTitle}
            </Typography>
          )}
        
          {/* Display thumbnail or video if available */}
          {(selectedPost.media) && (
            <Box 
              sx={{ 
                width: '100%', 
                maxHeight: hasVideo ? '500px' : '400px', 
                mb: 3, 
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {hasVideo && (
                <Box 
                  sx={{ 
                    width: '100%', 
                    position: 'relative',
                    backgroundColor: '#000',
                    '& video': {
                      width: '100%',
                      maxHeight: '500px',
                      objectFit: 'contain'
                    }
                  }}
                >
                  {(() => {
                    const videoUrl = selectedPost.media?.url || selectedPost.media_url;
                    console.log('Video Player Debug:', {
                      postId: selectedPost.document?.id,
                      videoUrl,
                      mediaType: selectedPost.media?.type || selectedPost.media_type,
                      format: selectedPost.format
                    });
                    return (
                      <video
                        controls
                        playsInline
                        preload="metadata"
                        width="100%"
                        poster={selectedPost.media?.thumbnail || selectedPost.media_thumbnail || selectedPost.thumbnail}
                      >
                        <source 
                          src={videoUrl}
                          type="video/mp4"
                        />
                        Your browser does not support HTML5 video.
                      </video>
                    );
                  })()}
                </Box>
              )}
            </Box>
          )}

          {/* Add PDF viewer if the post has a PDF document */}
          {hasPDF && (
            <Box sx={{ 
              mt: 3, 
              mb: 3,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 2, 
                borderBottom: '1px solid #e0e0e0',
                bgcolor: '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <DocumentIcon fontSize="small" />
                <Typography variant="subtitle2">
                  PDF Document
                </Typography>
              </Box>
              <PDFViewer url={selectedPost.media?.url} />
            </Box>
          )}

          {!hasVideo && thumbnailUrl && (
            <img 
              src={thumbnailUrl} 
              alt="Post media"
              style={{
                width: '100%',
                maxHeight: '400px',
                objectFit: 'contain',
                display: 'block'
              }}
              onError={(e) => {
                console.error("Image load error for URL:", thumbnailUrl);
                // Try with a different URL format if available
                if (!e.target.dataset.attempted && selectedPost.media?.url) {
                  e.target.dataset.attempted = 'true';
                  console.log("Attempting with alternate URL:", selectedPost.media.url);
                  e.target.src = selectedPost.media.url;
                } else if (!e.target.dataset.attemptedThumbnail && selectedPost.media?.thumbnail) {
                  e.target.dataset.attemptedThumbnail = 'true';
                  console.log("Attempting with thumbnail URL:", selectedPost.media.thumbnail);
                  e.target.src = selectedPost.media.thumbnail;
                } else {
                  // If all URLs fail, hide the image
                  e.target.style.display = 'none';
                  e.target.parentElement.style.display = 'none';
                }
              }}
            />
          )}

          <Typography 
            variant="body1" 
            sx={{ 
              whiteSpace: 'pre-line',
              lineHeight: 1.6,
              color: '#666',
              fontSize: '0.95rem'
            }}
          >
            {decodeHtmlEntities(postContent)}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3, borderTop: '1px solid #f0f0f0', pt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  backgroundColor: '#f7f9fa',
                  borderRadius: '12px',
                  padding: '4px 10px',
                  border: '1px solid #e0e0e0'
                }}
              >
                <PostReactions width={36} height={16} />
                <Typography variant="body2" sx={{ ml: 0.8, fontSize: '0.9rem', color: '#666' }}>
                  {formatNumber(postData.likes_count)}
              </Typography>
            </Box>
            </Box>
            
            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
              {formatNumber(postData.comments_count)} comments
            </Typography>
            
            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
              {selectedPost.stats?.reposts || 0} reposts
              </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: { xs: 2, sm: 2.5 }, pb: 2, pt: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CommentIcon />}
            onClick={() => handleOpenCommentDialog(selectedPost)}
            sx={{ 
              height: 44,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#0a66c2',
              color: '#0a66c2',
              '&:hover': {
                borderColor: '#004182',
                backgroundColor: 'rgba(10, 102, 194, 0.04)'
              },
            }}
          >
            Comment
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<RewriteIcon />}
            onClick={handleOpenRewritePanel}
            sx={{ 
              height: 44,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#0a66c2',
              color: '#0a66c2',
              '&:hover': {
                borderColor: '#004182',
                backgroundColor: 'rgba(10, 102, 194, 0.04)'
              },
            }}
          >
            Remix
          </Button>
          
          {postUrl && (
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(postUrl, '_blank', 'noopener,noreferrer')}
              sx={{ 
                height: 44,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#0a66c2',
                color: '#0a66c2',
                mr: 1,
                '&:hover': {
                  borderColor: '#004182',
                  backgroundColor: 'rgba(10, 102, 194, 0.04)'
                },
              }}
            >
              Open in LinkedIn
            </Button>
          )}
          
          <Button
            variant="contained"
            startIcon={<Share />}
            sx={{ 
              height: 44,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#0a66c2',
              '&:hover': {
                bgcolor: '#004182',
              },
            }}
          >
            Share
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderRewritePanel = () => {
    if (!selectedPost) return null;
    
    const postData = selectedPost.document;
    const author = selectedPost.data?.author || { name: 'LinkedIn User' };
    
    return (
      <Dialog
        open={openRewritePanel}
        onClose={handleCloseRewritePanel}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            p: 0,
            height: { xs: '95vh', md: '85vh' },
            maxHeight: '90vh',
            overflowY: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid var(--border-color)'
        }}>
          <Typography variant="h6" className="component-heading" sx={{ m: 0 }}>
            Rewrite Post with AI
          </Typography>
          <IconButton onClick={handleCloseRewritePanel} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ 
          p: 0, 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          height: '100%',
          overflow: 'hidden'
        }}>
          {/* Original post - left side */}
          <Box sx={{ 
            width: { xs: '100%', md: '50%' }, 
            p: 3,
            overflowY: 'auto',
            borderRight: { xs: 'none', md: '1px solid var(--border-color)' },
            borderBottom: { xs: '1px solid var(--border-color)', md: 'none' },
            height: { xs: '40%', md: '100%' }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar 
                src={author.profilePicture || (author.image_base64 ? `data:image/jpeg;base64,${author.image_base64}` : '')}
                alt={author.name}
                sx={{ width: 40, height: 40, mr: 1.5 }}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.2, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                  {author.name}
                </Typography>
                <Typography variant="caption" color="var(--text-light)" sx={{ lineHeight: 1.3, display: 'block', mt: 0.3 }}>
                  {author.occupation || author.summary || 'LinkedIn User'}
                </Typography>
              </Box>
            </Box>
            
            <Typography 
              variant="body1" 
              sx={{ 
                whiteSpace: 'pre-line',
                lineHeight: 1.6,
                color: 'var(--text-primary)',
                fontSize: '0.95rem'
              }}
            >
              {decodeHtmlEntities(postData.body)}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ThumbUp fontSize="small" sx={{ color: '#0a66c2', mr: 0.5, fontSize: '0.9rem' }} />
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  {formatNumber(postData.likes_count)} Likes
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ChatBubbleOutline fontSize="small" sx={{ color: '#666', mr: 0.5, fontSize: '0.9rem' }} />
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  {formatNumber(postData.comments_count)} Comments
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Rewrite content - right side */}
          <Box sx={{ 
            width: { xs: '100%', md: '50%' }, 
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            height: { xs: '60%', md: '100%' },
            overflow: 'hidden'
          }}>
            <Typography className="section-title" sx={{ mb: 1 }}>
              Topic for Rewrite (optional)
            </Typography>
            
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter a topic to adapt this post for (or leave empty to just improve it)"
              value={rewriteTopic}
              onChange={(e) => setRewriteTopic(e.target.value)}
              size="small"
              className="form-field"
              sx={{ mb: 2 }}
            />
            
            <Button
              fullWidth
              variant="contained"
              startIcon={rewriteLoading ? <CircularProgress size={20} color="inherit" /> : <RewriteIcon />}
              onClick={handleRewriteWithAI}
              disabled={rewriteLoading || !selectedPost}
              sx={{ 
                mb: 3,
                height: 48,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: 'var(--primary-color)',
                '&:hover': {
                  bgcolor: 'var(--primary-dark)',
                },
              }}
            >
              {rewriteLoading ? 'Generating...' : 'Generate Rewrite'}
            </Button>
            
            <Typography className="section-title" sx={{ mb: 1 }}>
              Result:
            </Typography>
            
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2.5, 
                flex: 1,
                overflowY: 'auto',
                bgcolor: rewrittenContent ? '#ffffff' : '#f9fafb',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#a1a1a1',
                },
                border: '1px solid var(--border-color)',
                borderRadius: '8px'
              }}
            >
              {rewriteLoading ? (
                <>
                  <Skeleton width="100%" height={24} />
                  <Skeleton width="100%" height={24} />
                  <Skeleton width="95%" height={24} />
                  <Skeleton width="90%" height={24} />
                  <Skeleton width="100%" height={24} />
                  <Skeleton width="80%" height={24} />
                </>
              ) : rewrittenContent ? (
                <Typography variant="body1" whiteSpace="pre-line" lineHeight={1.6} sx={{ color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                  {decodeHtmlEntities(rewrittenContent)}
                </Typography>
              ) : (
                <Typography variant="body2" color="var(--text-light)" sx={{ textAlign: 'center', mt: 4, fontSize: '0.85rem' }}>
                  Enter a topic and click "Generate Rewrite" to see the AI-generated version here
                </Typography>
              )}
            </Paper>
            
            {rewrittenContent && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={handleCopyRewrite}
                  sx={{
                    height: 44,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'var(--primary-color)',
                    borderColor: 'var(--primary-color)',
                    '&:hover': {
                      borderColor: 'var(--primary-dark)',
                      bgcolor: alpha('#4890fe', 0.04),
                    },
                  }}
                >
                  Copy
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    height: 44,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: 'var(--primary-color)',
                    '&:hover': {
                      bgcolor: 'var(--primary-dark)',
                    },
                  }}
                >
                  Post to LinkedIn
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  // Add function to generate comment with AI
  const handleGenerateComment = async () => {
    if (!commentingPost) return;
    
    setGeneratingComment(true);
    try {
      const postData = {
        post: commentingPost.document.body,
        prompt: commentPrompts[selectedCommentPrompt],
        post_owner: commentingPost.data?.author?.name || 'LinkedIn User',
        word_limit: 100
      };
      
      // Call Azure OpenAI API (similar to the handleRewriteWithAI function)
      const response = await fetch('https://eai.openai.azure.com/openai/deployments/test/chat/completions?api-version=2024-02-15-preview', {
        method: 'POST',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/135.0.0.0 Safari/537.36 Edg/135.0.0.0',
          'Accept': 'application/json',
          'Accept-Encoding': 'gzip, deflate, br, zstd',
          'api-key': process.env.REACT_APP_OPENAI_API_KEY,
          'content-type': 'application/json;charset=UTF-8',
          'origin': `chrome-extension://${process.env.REACT_APP_CHROME_EXTENSION_ID}`
        },
        body: JSON.stringify({
          "model": "gpt-4",
          "messages": [
            {
              "role": "user",
              "content": `${postData.prompt}\n\nLinkedIn Post:\n${postData.post}`
            }
          ],
          "temperature": 0.7,
          "max_tokens": 200
        })
      });
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('AI Comment Response:', data);
      
      // Extract the generated comment
      const generatedComment = data.choices[0].message.content.trim();
      
      // Clean the comment text - remove quotes at beginning and end if present
      let cleanedComment = generatedComment;
      if (cleanedComment.startsWith('"') && cleanedComment.endsWith('"')) {
        cleanedComment = cleanedComment.substring(1, cleanedComment.length - 1);
      }
      
      // Set the comment text
      setCommentText(cleanedComment);
      
      enqueueSnackbar('Comment generated successfully!', { variant: 'success' });
    } catch (error) {
      console.error('Error generating comment:', error);
      enqueueSnackbar('Error generating comment. Please try again.', { variant: 'error' });
    } finally {
      setGeneratingComment(false);
    }
  };
  
  const renderCommentDialog = () => {
    if (!commentingPost) return null;
    
    const postData = commentingPost.document;
    const author = commentingPost.data?.author || { name: 'LinkedIn User' };
    const isCommentTooLong = commentText.length > 700;
    
    return (
      <Dialog
        open={openCommentDialog}
        onClose={handleCloseCommentDialog}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            p: 0,
            height: { xs: '95vh', md: '85vh' },
            maxHeight: '90vh',
            overflowY: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid var(--border-color)'
        }}>
          <Typography variant="h6" className="component-heading" sx={{ m: 0 }}>
            Comment on Post
          </Typography>
          <IconButton onClick={handleCloseCommentDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ 
          p: 0, 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          height: '100%',
          overflow: 'hidden'
        }}>
          {/* Original post - left side */}
          <Box sx={{ 
            width: { xs: '100%', md: '50%' }, 
            p: 3,
            overflowY: 'auto',
            borderRight: { xs: 'none', md: '1px solid var(--border-color)' },
            borderBottom: { xs: '1px solid var(--border-color)', md: 'none' },
            height: { xs: '40%', md: '100%' }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar 
                src={author.profilePicture || (author.image_base64 ? `data:image/jpeg;base64,${author.image_base64}` : '')}
                alt={author.name}
                sx={{ width: 40, height: 40, mr: 1.5 }}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.2, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                  {author.name}
                </Typography>
                <Typography variant="caption" color="var(--text-light)" sx={{ lineHeight: 1.3, display: 'block', mt: 0.3 }}>
                  {author.occupation || author.summary || 'LinkedIn User'}
                </Typography>
              </Box>
            </Box>
            
            <Typography 
              variant="body1" 
              sx={{ 
                whiteSpace: 'pre-line',
                lineHeight: 1.6,
                color: 'var(--text-primary)',
                fontSize: '0.95rem'
              }}
            >
              {decodeHtmlEntities(postData.body)}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ThumbUp fontSize="small" sx={{ color: '#0a66c2', mr: 0.5, fontSize: '0.9rem' }} />
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  {formatNumber(postData.likes_count)} Likes
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ChatBubbleOutline fontSize="small" sx={{ color: '#666', mr: 0.5, fontSize: '0.9rem' }} />
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  {formatNumber(postData.comments_count)} Comments
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Comment section - right side */}
          <Box sx={{ 
            width: { xs: '100%', md: '50%' }, 
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            height: { xs: '60%', md: '100%' },
            overflow: 'hidden'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography className="section-title" sx={{ mb: 0 }}>
                Write your comment
              </Typography>
              
              {/* AI Comment Generator Controls - Updated with modern styling */}
              <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                <FormControl size="small" sx={{ minWidth: 150, mr: 1 }}>
                  <Select
                    value={selectedCommentPrompt}
                    onChange={(e) => setSelectedCommentPrompt(e.target.value)}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    }}
                    sx={{ 
                      borderRadius: 2,
                      fontSize: '0.85rem',
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--border-color)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0a66c2',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0a66c2',
                      }
                    }}
                  >
                    {Object.keys(commentPrompts).map((promptType) => (
                      <MenuItem 
                        key={promptType} 
                        value={promptType}
                        sx={{ fontSize: '0.85rem' }}
                      >
                        {promptType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Button
                  variant="contained"
                  onClick={handleGenerateComment}
                  disabled={generatingComment}
                  startIcon={generatingComment ? <CircularProgress size={16} color="inherit" /> : <RewriteIcon fontSize="small" />}
                  sx={{ 
                    height: 38,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: '#0a66c2',
                    '&:hover': {
                      bgcolor: '#004182',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(10, 102, 194, 0.4)',
                      color: 'white'
                    }
                  }}
                >
                  {generatingComment ? 'Generating...' : 'AI Comment'}
                </Button>
              </Box>
            </Box>
            
            <TextField
              autoFocus
              multiline
              rows={8}
              variant="outlined"
              fullWidth
              placeholder="Add your thoughts on this post..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              error={isCommentTooLong}
              helperText={isCommentTooLong ? "Comment exceeds LinkedIn's maximum character limit" : ""}
              sx={{
                flex: 1,
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.95rem'
                }
              }}
            />
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mt: 'auto',
              pt: 2,
              borderTop: '1px solid var(--border-color)'
            }}>
              <Typography 
                variant="caption" 
                color={isCommentTooLong ? "error" : "textSecondary"} 
                sx={{ fontWeight: isCommentTooLong ? 600 : 400 }}
              >
                {commentText.length}/700 characters
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  onClick={handleCloseCommentDialog}
                  variant="outlined"
                  sx={{ 
                    height: 44,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-secondary)',
                    '&:hover': {
                      borderColor: '#004182',
                      bgcolor: 'rgba(0, 65, 130, 0.04)'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={commentLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  onClick={handlePostComment}
                  disabled={!commentText.trim() || commentLoading || isCommentTooLong}
                  sx={{ 
                    height: 44,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: '#0a66c2',
                    '&:hover': {
                      bgcolor: '#004182',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(10, 102, 194, 0.4)',
                      color: 'white'
                    }
                  }}
                >
                  {commentLoading ? 'Posting...' : 'Post Comment'}
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  // Function to sort an array of LinkedIn posts
  const sortPostsArray = (postsArray, sortMethod) => {
    if (!postsArray || postsArray.length === 0) return [];
    
    const postsCopy = [...postsArray];
    
    switch(sortMethod) {
      case 'most_likes':
        return postsCopy.sort((a, b) => (b.stats?.likes || 0) - (a.stats?.likes || 0));
      case 'most_comments':
        return postsCopy.sort((a, b) => (b.stats?.comments || 0) - (a.stats?.comments || 0));
      case 'recent':
      default:
        return postsCopy.sort((a, b) => {
          const dateA = a.postedAt?.timestamp || 0;
          const dateB = b.postedAt?.timestamp || 0;
          return dateB - dateA; // Descending order - newest first
        });
    }
  };
  
  // Handle sort change
  const handleSortChange = (event) => {
    const newSortOption = event.target.value;
    if (newSortOption !== sortOption) {
      setSortOption(newSortOption);
      // Reset to page 1 when changing sort order
      setPage(1);
      // The useEffect will handle fetching posts with the new sort and page
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage !== page && newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      // fetchPosts will be triggered by the useEffect
      
      // Scroll to top when changing pages
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Conditional rendering based on subscription status
  if (subscriptionStatusLoading) {
    return <LoadingScreen message="Loading Viral Posts..." />;
  }

  // Main component rendering logic - kept separate for clarity
  const renderMainContent = () => (
    <Container 
      maxWidth={false} 
      sx={{ 
        py: 3, 
        position: 'relative', 
        px: { xs: 2, sm: 3, md: 4 },  // Increased padding
        overflow: 'visible',
        maxWidth: '100vw'
      }}
    >
      <Paper className="content-paper" sx={{ 
        p: { xs: 2, sm: 2.5, md: 3 }, 
        mb: 3, 
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box>
            <Typography
              variant="h5"
              component="h1"
              className="component-heading"
              sx={{ color: 'black', fontWeight: 600, mb: 0.5 }}
            >
              LinkedIn Posts
            </Typography>
            <Typography variant="body1" color="var(--text-secondary)">
              Discover and analyze viral LinkedIn content
            </Typography>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, sm: 2 }, 
            flexWrap: 'wrap', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            width: '100%'
          }}>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 2 }, 
              flexWrap: 'wrap', 
              flex: 1,
              width: '100%'
            }}>
              <TextField
                placeholder="Search posts..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  )
                }}
                sx={{ 
                  flex: { xs: '1 1 100%', sm: '1 1 auto' },
                  minWidth: { sm: '300px' },
                  maxWidth: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Post Type</InputLabel>
                <Select
                  value={filters?.format || 'all'}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, format: e.target.value }));
                    setPage(1); // Reset to first page when changing filter
                    fetchPosts(true); // Fetch posts with new filter
                  }}
                  label="Post Type"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="Image">Images</MenuItem>
                  <MenuItem value="Video">Videos</MenuItem>
                  <MenuItem value="Document">Documents & PDFs</MenuItem>
                  <MenuItem value="Text">Text Only</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortOption}
                  onChange={handleSortChange}
                  label="Sort By"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="recent">Most Recent</MenuItem>
                  <MenuItem value="most_likes">Most Likes</MenuItem>
                  <MenuItem value="most_comments">Most Comments</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ 
              whiteSpace: 'nowrap',
              display: { xs: 'none', md: 'block' }
            }}>
              Page {page} of {totalPages} ({posts.length > 0 ? `${((page - 1) * 24) + 1}-${Math.min(page * 24, totalPages * 24)}` : '0'} of {totalPages * 24} posts)
            </Typography>
          </Box>
        </Box>
      </Paper>
      
      {/* Add Category Filter Chips */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 1, 
        mb: 3,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        {categories.map(category => (
          <Chip
            key={category.id}
            label={category.label}
            onClick={() => handleCategoryClick(category.id)}
            variant={selectedCategory === category.id ? "filled" : "outlined"}
            sx={{
              borderRadius: 2,
              bgcolor: selectedCategory === category.id ? '#4890fe' : 'transparent',
              color: selectedCategory === category.id ? 'white' : '#4A5568',
              borderColor: selectedCategory === category.id ? '#4890fe' : '#E2E8F0',
              '&:hover': {
                bgcolor: selectedCategory === category.id ? '#3a7ad9' : 'rgba(0, 0, 0, 0.04)'
              },
              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
              height: { xs: '28px', sm: '32px' },
              '& .MuiChip-label': {
                px: { xs: 1, sm: 1.5 }
              },
              maxWidth: '100%'
            }}
          />
        ))}
      </Box>
      
      {error ? (
        <Paper className="content-paper" sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <Typography color="error">{error}</Typography>
          <Button 
            startIcon={<RefreshIcon />} 
            onClick={() => fetchPosts()}
            variant="contained"
            sx={{ 
              mt: 2,
              height: 48,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#4890fe',
              '&:hover': {
                bgcolor: '#3a7fd5',
              },
            }}
          >
            Try Again
          </Button>
        </Paper>
      ) : (
        <Box sx={{ mb: 4, width: '100%', overflowX: 'hidden' }}>
          <Masonry
            breakpointCols={{
              default: 4,  // 4 columns only for very large screens
              1800: 3,     // 3 columns for large screens (increased from 1500)
              1300: 2,     // 2 columns for medium screens (increased from 1100)
              800: 1       // 1 column for small screens (increased from 700)
            }}
            className="masonry-grid"
            columnClassName="masonry-grid_column"
          >
            {loading ? (
              // Loading state - show skeleton cards (12 items to match posts per page)
              Array.from(new Array(12)).map((_, index) => (
                <div key={index}>
                  <Paper sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    height: '320px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    width: '100%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={24} />
                        <Skeleton variant="text" width="40%" height={16} />
            </Box>
                    </Box>
                    <Skeleton variant="text" width="90%" height={24} />
                    <Skeleton variant="text" width="100%" height={20} />
                    <Skeleton variant="text" width="95%" height={20} />
                    <Skeleton variant="text" width="90%" height={20} />
                    <Skeleton variant="rectangular" width="100%" height={140} sx={{ borderRadius: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 'auto' }}>
                      <Skeleton variant="text" width="50%" height={24} />
                      <Skeleton variant="circular" width={32} height={32} />
                    </Box>
                  </Paper>
                </div>
              ))
            ) : (
              // Loaded posts
              posts.map((post, index) => (
                <div 
                  key={post.document.id}
                >
                  {renderPostCard(post)}
                </div>
              ))
            )}
          </Masonry>
          
          {/* Pagination Controls - only show when not loading */}
          {!loading && posts.length > 0 && (
            <Paper 
              elevation={0} 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                mt: 4, 
                mb: 3,
                p: { xs: 1, sm: 2 },
                gap: { xs: 0.5, sm: 1, md: 1.5 },
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                flexWrap: 'wrap'
              }}
            >
              <Button
                disabled={page === 1 || loading}
                onClick={() => handlePageChange(1)}
                variant="outlined"
                sx={{ 
                  minWidth: { xs: '50px', sm: '60px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  p: { xs: '4px 8px', sm: '6px 16px' },
                  borderColor: '#e0e0e0',
                  color: '#616161',
                  '&.Mui-disabled': {
                    color: '#bdbdbd',
                    borderColor: '#e0e0e0'
                  }
                }}
              >
                First
              </Button>
              
              <Button
                disabled={page === 1 || loading}
                onClick={() => handlePageChange(page - 1)}
                variant="outlined"
                sx={{ 
                  minWidth: { xs: '50px', sm: '60px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  p: { xs: '4px 8px', sm: '6px 16px' },
                  borderColor: '#e0e0e0',
                  color: '#616161',
                  '&.Mui-disabled': {
                    color: '#bdbdbd',
                    borderColor: '#e0e0e0'
                  }
                }}
              >
                Prev
              </Button>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                px: { xs: 1, sm: 2, md: 3 },
                py: 1,
                borderRadius: 1,
                bgcolor: '#f5f5f5',
                mx: { xs: 0.5, sm: 1 }
              }}>
                <Typography variant="body1" sx={{ 
                  fontWeight: 500, 
                  color: '#424242',
                  fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } 
                }}>
                  Page {page} of {totalPages}
                </Typography>
              </Box>
              
              <Button
                disabled={page >= totalPages || loading}
                onClick={() => handlePageChange(page + 1)}
                variant="outlined"
                sx={{ 
                  minWidth: { xs: '50px', sm: '60px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  p: { xs: '4px 8px', sm: '6px 16px' },
                  borderColor: '#e0e0e0',
                  color: '#616161',
                  '&.Mui-disabled': {
                    color: '#bdbdbd',
                    borderColor: '#e0e0e0'
                  }
                }}
              >
                Next
              </Button>
              
              <Button
                disabled={page >= totalPages || loading}
                onClick={() => handlePageChange(totalPages)}
                variant="outlined"
                sx={{ 
                  minWidth: { xs: '50px', sm: '60px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  p: { xs: '4px 8px', sm: '6px 16px' },
                  borderColor: '#e0e0e0',
                  color: '#616161',
                  '&.Mui-disabled': {
                    color: '#bdbdbd',
                    borderColor: '#e0e0e0'
                  }
                }}
              >
                Last
              </Button>
            </Paper>
          )}
        </Box>
      )}
      
      {showScrollTop && (
        <Fab 
          color="primary" 
          size="small" 
          aria-label="scroll back to top"
          onClick={handleScrollToTop}
          sx={{ 
            position: 'fixed', 
            bottom: 16, 
            right: 16,
            zIndex: 1000,
            bgcolor: 'var(--primary-color)',
            '&:hover': {
              bgcolor: 'var(--primary-dark)'
            }
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}
      
      {renderPostDetails()}
      {renderRewritePanel()}
      {renderCommentDialog()}
    </Container>
  );

  if (!isFeatureAllowed) {
    // Render minimal skeleton UI covering the viewport
    return (
      <Box sx={{ 
        position: 'relative', 
        height: 'calc(100vh - 64px)', // Adjust 64px based on your header/app bar height
        overflow: 'hidden'
      }}>
        {/* Apply opacity to the skeleton container */}
        <Box sx={{ opacity: 0.4, height: '100%', width: '100%', overflow: 'hidden' }}>
          <Container 
            maxWidth={false} 
            sx={{ 
              py: 3, 
              width: '100%', 
              maxWidth: '100vw',
              overflow: 'hidden',
              boxSizing: 'border-box',
              px: { xs: 1, sm: 2, md: 3 }
            }}
          >
            {/* Skeleton Header */}
            <Paper className="content-paper" sx={{ p: 3, mb: 3, bgcolor: 'transparent', boxShadow: 'none' }}>
              <Skeleton animation="wave" variant="text" width="30%" height={40} sx={{ mb: 1 }} />
              <Skeleton animation="wave" variant="text" width="60%" height={20} sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Skeleton animation="wave" variant="rectangular" width={200} height={40} sx={{ borderRadius: 1 }} />
                <Skeleton animation="wave" variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
                <Skeleton animation="wave" variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
              </Box>
            </Paper>
            
            {/* Skeleton Category Chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {Array.from(new Array(8)).map((_, index) => (
                <Skeleton key={index} animation="wave" variant="rectangular" width={100} height={32} sx={{ borderRadius: 4 }} />
              ))}
            </Box>

            {/* Skeleton Post Grid */}
            <Grid container spacing={3}>
              {Array.from(new Array(6)).map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper sx={{ p: 2, borderRadius: 2, height: 280 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Skeleton animation="wave" variant="circular" width={40} height={40} />
                      <Box sx={{ ml: 1, flex: 1 }}>
                        <Skeleton animation="wave" variant="text" width="60%" />
                        <Skeleton animation="wave" variant="text" width="40%" />
                      </Box>
                    </Box>
                    <Skeleton animation="wave" variant="text" height={80} />
                    <Skeleton animation="wave" variant="text" />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                      <Skeleton animation="wave" variant="text" width="40%" />
                      <Skeleton animation="wave" variant="circular" width={30} height={30} />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
        
        {/* Overlay remains centered on top */}
        <FeatureLockOverlay featureName="Viral LinkedIn Posts" currentPlan={currentPlanId} />
      </Box>
    );
  }

  // If feature is allowed, render content normally
  // Handle loading state for actual posts data if the feature is allowed
  if (loading && posts.length === 0 && isFeatureAllowed) { // Check isFeatureAllowed here
    return <LoadingScreen message="Loading Viral Posts..." />;
  }
  
  // Wrap the renderMainContent call in an overflow-controlling container
  return (
    <Box sx={{ width: '100%', maxWidth: '100vw', overflow: 'hidden', boxSizing: 'border-box' }}>
      <Helmet>
        <title>Viral LinkedIn Posts | Instation</title>
        <meta name="description" content="Discover and analyze viral LinkedIn posts to improve your content strategy." />
      </Helmet>
      
      {renderMainContent()}
    </Box>
  );
}; // End of the ViralLinkedInPosts component

export default ViralLinkedInPosts; 